import { Grid, Title } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';
import { messages } from '../messages';
import { useGetActivePlansQuery, useGetPaymentMethodsQuery } from 'api/subscriptionPlanEndpoints';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import { Loading } from '@carbon/react';
import { SubscriptionPlanDto } from 'api/dtos/subscriptionPlanDtos';
import useGetCurrentSubscription from 'Screens/Dashboard/Subscription/useGetCurrentSubscription';
import SubscribeModal from 'Screens/Dashboard/Subscription/SubscribeModal/SubscribeModal';
import { useState } from 'react';
import SubscribeFreePlanModal from 'Screens/Dashboard/Subscription/SubscribeFreePlanModal/SubscribeFreePlanModal';
import SubscriptionPlanCard from 'Components/Common/SubscriptionPlanCard/SubscriptionPlanCard';

export const SubscriptionPlans = () => {
    const intl = useIntl();
    const { activeSubscription } = useGetCurrentSubscription(); 
    
    const { data: subscriptionPlans, isFetching, refetch } = useGetActivePlansQuery();
    const { data: paymentMethods, isFetching: isPaymentMethodsFetching } = useGetPaymentMethodsQuery();
    const hasPaymentMethod = Boolean(paymentMethods?.length);

    const [requestedSubscription, setRequestedSubscription] = useState<SubscriptionPlanDto | null>(null);

    const onChoosePlan = (subscription: SubscriptionPlanDto) => {
        setRequestedSubscription(subscription);
    };

    const onCloseRequestedSubscription = () => {
        setRequestedSubscription(null);
    };

    if (!subscriptionPlans) {
        return <ErrorActionState onClick={refetch} />;
    }

    if (isFetching || isPaymentMethodsFetching) {
        return <Loading />;
    }

    return (
        <>
            <Title order={2} size="h3" mb="md">
                {intl.formatMessage(messages.availablePlans)}
            </Title>
            <Grid>
                {subscriptionPlans.map((plan, index) => {
                    const isCurrentPlan = plan.id === activeSubscription?.planId;
                    const requiresPaymentMethod = plan.maxEntities !== 0;
                    const isDisabledByMissingPaymentMethod = requiresPaymentMethod && !hasPaymentMethod && !isCurrentPlan;

                    return (
                        <Grid.Col key={plan.id} span={{ base: 12, sm: 6, md: 4 }}>
                            <SubscriptionPlanCard
                                plan={plan}
                                highlighted={isCurrentPlan}
                                popular={index === 1}
                                popularLabel={intl.formatMessage(messages.mostPopular)}
                                monthLabel={intl.formatMessage(messages.month)}
                                actionDisabled={isCurrentPlan || isDisabledByMissingPaymentMethod}
                                actionDisabledTooltip={isDisabledByMissingPaymentMethod
                                    ? intl.formatMessage(subscriptionPlanMessages.paymentMethodRequired)
                                    : undefined}
                                actionVariant={isCurrentPlan ? 'filled' : 'outline'}
                                actionLabel={(() => {
                                    if (isCurrentPlan) {
                                        return intl.formatMessage(messages.current);
                                    }

                                    if (plan.maxEntities === 0) {
                                        return intl.formatMessage(messages.lower);
                                    }

                                    return intl.formatMessage(messages.choosePlan);
                                })()}
                                onAction={() => { onChoosePlan(plan); }}
                                maxVisibleFeatures={plan.features.length}
                            />
                        </Grid.Col>
                    );
                })}
            </Grid>
            {/* To_do */}
            {(requestedSubscription && requestedSubscription.maxEntities === 0) && <SubscribeFreePlanModal confirmationModalOpen onModalClose={onCloseRequestedSubscription} />}
            {(requestedSubscription && requestedSubscription.maxEntities !== 0) && <SubscribeModal opened onClose={onCloseRequestedSubscription} subscriptionPlan={requestedSubscription} />}
        </>
    );
};

const subscriptionPlanMessages = defineMessages({
    paymentMethodRequired: {
        id: 'src.Screens.Dashboard.Subscription.SubscriptionPlans.paymentMethodRequired',
        defaultMessage: 'Żeby zasubskrybować ten plan, najpierw dodaj metodę płatności.',
    },
});
